// theme: sponsors should look like part of the site. They inherit its font and text colour; the
// background behind the slot decides light or dark, and the site's link colour (or the owner's accent)
// becomes the button colour, as long as it stays readable.

/** @typedef {[number, number, number, number]} RGBA  red, green, blue 0–255, alpha 0–1 */

const LIGHT = /** @type {RGBA} */ ([255, 255, 255, 1]);
const DARK = /** @type {RGBA} */ ([24, 24, 27, 1]);

/** @type {CanvasRenderingContext2D | null | undefined} */
let paint;
/** @type {Map<string, RGBA>} */
const parsed = new Map();

/**
 * Any CSS colour as RGBA. The browser parses it by painting one pixel, so modern formats like oklch,
 * lab and color() work too (Tailwind v4 sites use oklch everywhere). Unknown colours are transparent.
 * @param {string} color
 * @returns {RGBA}
 */
export function rgba(color) {
    let c = parsed.get(color);
    if (!c) {
        paint ??= Object.assign(document.createElement('canvas'), { width: 1, height: 1 }).getContext('2d', { willReadFrequently: true });
        c = [0, 0, 0, 0];
        if (paint) {
            paint.clearRect(0, 0, 1, 1);
            paint.fillStyle = 'transparent';
            paint.fillStyle = color;
            paint.fillRect(0, 0, 1, 1);
            const d = paint.getImageData(0, 0, 1, 1).data;
            c = [d[0], d[1], d[2], d[3] / 255];
        }
        parsed.set(color, c);
    }
    return c;
}

/** Perceived brightness, 0 (black) to 1 (white). @param {RGBA} c */
export const lum = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

/** WCAG relative luminance (sRGB channels made linear), 0 to 1. @param {RGBA} c */
const light = c => c.slice(0, 3).reduce((sum, v, i) => sum + [0.2126, 0.7152, 0.0722][i] * ((v /= 255) <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4), 0);

/**
 * The solid colour behind an element, or null when a background image or gradient comes first.
 * @param {Element | null} e
 * @returns {RGBA | null}
 */
function background(e) {
    for (; e; e = e.parentElement) {
        const cs = getComputedStyle(e), c = rgba(cs.backgroundColor);
        if (c[3] > 0.5) return c;
        if (cs.backgroundImage !== 'none') return null;
    }
    return LIGHT;
}

/** The site's link colour: the first visible link in its main content that isn't ours. */
function siteLink() {
    const main = document.querySelector('article, main, [role=main]') || document.body;
    for (const a of main.querySelectorAll('a[href]')) {
        if (!a.closest('sl-slot') && a.getClientRects().length) return getComputedStyle(a).color;
    }
    return '';
}

/**
 * The button colour: the site's own accent when it reads well on this background, else plain ink.
 * @param {RGBA | null} accent  the owner's accent, else the site's link colour (null: the site has none)
 * @param {RGBA} back  the background the button sits on
 * @param {boolean} dark  whether the slot sits on a dark background
 * @returns {string} a CSS colour
 */
function buttonColor(accent, back, dark) {
    // a (nearly) transparent link colour isn't a colour; one too close to the background isn't readable
    if (accent && accent[3] >= 0.5 && Math.abs(lum(accent) - lum(back)) >= 0.25) return `rgb(${accent[0]} ${accent[1]} ${accent[2]})`;
    return dark ? '#f4f4f5' : '#18181b';
}

/**
 * Colours for every slot on the page. Call once per render: the site's link colour is read once.
 * @param {import('./config.js').Look} look
 * @returns {(box: HTMLElement) => { dark: boolean, vars: Record<string, string> }}
 */
export function themer(look) {
    const accent = look.accent || siteLink();
    return box => {
        const bg = background(box.parentElement);
        const dark = look.mode ? look.mode === 'dark' : bg ? lum(bg) < 0.45 : lum(rgba(getComputedStyle(box).color)) > 0.6;
        const btn = buttonColor(accent ? rgba(accent) : null, look.mode || !bg ? (dark ? DARK : LIGHT) : bg, dark);
        return {
            dark,
            vars: {
                '--sl-r': look.radius === 'square' ? '2px' : '8px',
                '--sl-btn': btn,
                // white while it keeps at least 3:1 on the button (luminance up to 0.30), as brand buttons do:
                // pure "higher contrast" picks black on mid greens, blues and reds, which reads as a mistake.
                // Paler buttons (yellow, orange, light accents) get near-black.
                '--sl-btnfg': light(rgba(btn)) > 0.3 ? '#111' : '#fff',
                // forced light or dark: boxed formats get solid colours instead of a tint over the site
                '--sl-card': look.mode ? (dark ? '#18181b' : '#fff') : '',
                '--sl-fg': look.mode ? (dark ? '#f4f4f5' : '#18181b') : '',
            },
        };
    };
}
